import React, { useEffect, useMemo, useState } from 'react';
import { Alert, InputNumber, Radio, Select, Space, Spin, Tag, Typography } from 'antd';
import lib from '../../assets/utils/lib';
import { ageRangeLabel } from './premiumTypes';

const { Text } = Typography;
const fmt = (v) => `₦${Number(v || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;

/**
 * The purchase flow from "Premium Types, Sub-Bands & Additional Dependent
 * Pricing" §3, as one control:
 *
 *   1. premium type: Individual or Family
 *   2. if Family: a family sub-band offered for the plan (and organization)
 *   3. optional dependents: a quantity per dependent category
 *   4. the total, computed by the server (POST /admin/pm/quote), shown live
 *
 * Emits onChange({ base_band_id, dependents:[{band_id, qty}], quote,
 * premium_type, additional_heads, enrolee_size }) — the caller sends
 * base_band_id as pm_band_id and dependents as pm_dependents; premium_type,
 * additional_heads and enrolee_size keep the legacy pipeline consistent.
 *
 * Renders null (and emits nothing) when the plan has no sub-band prices, so
 * callers fall back to their legacy fields.
 */
export default function SubBandPurchasePicker({ planId, socialNo, orgId, value, onChange, onAvailability, compact = false }) {
  const [offered, setOffered] = useState(null);
  const [loading, setLoading] = useState(false);
  const [premiumType, setPremiumType] = useState(value?.premium_type || 'individual');
  const [familyBandId, setFamilyBandId] = useState(value?.base_band_id || null);
  const [deps, setDeps] = useState(() => {
    const m = {};
    (value?.dependents || []).forEach((d) => { m[d.band_id] = d.qty; });
    return m;
  });
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!planId) { setOffered(null); return undefined; }
    setLoading(true);
    lib.pmOfferedBands({ planId, socialNo, orgId }).then((data) => {
      if (!alive) return;
      const ok = !!(data && Array.isArray(data.bands) && data.bands.length);
      setOffered(ok ? data : null);
      setLoading(false);
      if (onAvailability) onAvailability(ok);
    });
    return () => { alive = false; };
  }, [planId, socialNo, orgId]);

  const groups = offered?.premium_types || { individual: [], family: [], dependent: [] };
  const individualBand = groups.individual?.[0] || null;
  const familyBands = groups.family || [];
  const dependentBands = groups.dependent || [];

  // Keep the family selection valid for the offered set.
  useEffect(() => {
    if (premiumType === 'family' && familyBands.length && !familyBands.some((b) => b.band_id === familyBandId)) {
      setFamilyBandId(familyBands[0].band_id);
    }
  }, [premiumType, familyBands, familyBandId]);

  const baseBandId = premiumType === 'individual' ? individualBand?.band_id || null : familyBandId;
  const dependents = useMemo(
    () => Object.entries(deps).filter(([, q]) => Number(q) > 0).map(([band_id, qty]) => ({ band_id: Number(band_id), qty: Number(qty) })),
    [deps],
  );

  // Server-side quote, live.
  useEffect(() => {
    let alive = true;
    if (!planId || !baseBandId) { setQuote(null); return undefined; }
    lib.pmQuote({ plan_id: planId, ...(orgId ? { org_id: orgId } : {}), ...(socialNo ? { social_no: socialNo } : {}), base_band_id: baseBandId, dependents })
      .then((res) => {
        if (!alive) return;
        if (res?.error) { setQuote(null); setQuoteError(res.message); return; }
        setQuoteError(null);
        setQuote(res.data);
      });
    return () => { alive = false; };
  }, [planId, orgId, socialNo, baseBandId, dependents]);

  // Emit upward whenever the composition or the quote changes.
  useEffect(() => {
    if (!onChange || !offered) return;
    const additionalHeads = dependents.reduce((n, d) => n + d.qty, 0);
    onChange({
      base_band_id: baseBandId,
      dependents,
      quote,
      premium_type: premiumType,
      additional_heads: additionalHeads,
      enrolee_size: quote?.members ?? null,
      policy_cost: quote?.total ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseBandId, dependents, quote, premiumType, offered]);

  if (!planId) return null;
  if (loading && !offered) return <Spin size="small" />;
  if (!offered) return null;

  return (
    <div className="pm-subband-picker">
      <Space direction="vertical" style={{ width: '100%' }} size={compact ? 6 : 10}>
        <div>
          <Text strong style={{ fontSize: 12 }}>Premium type</Text>
          <div>
            <Radio.Group
              value={premiumType}
              onChange={(e) => setPremiumType(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
              options={[
                { value: 'individual', label: individualBand ? `Individual · ${fmt(individualBand.effective_price)}` : 'Individual', disabled: !individualBand },
                { value: 'family', label: 'Family', disabled: !familyBands.length },
              ]}
            />
          </div>
        </div>

        {premiumType === 'family' ? (
          <div>
            <Text strong style={{ fontSize: 12 }}>Family size</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={familyBandId}
              onChange={setFamilyBandId}
              options={familyBands.map((b) => ({
                value: b.band_id,
                label: `${b.name} — ${fmt(b.effective_price)}${b.override_price !== null ? ' (negotiated)' : ''}`,
              }))}
            />
          </div>
        ) : null}

        {dependentBands.length ? (
          <div>
            <Text strong style={{ fontSize: 12 }}>Additional dependents</Text>
            <Text type="secondary" style={{ fontSize: 11 }}> — optional, any quantity per category</Text>
            {dependentBands.map((b) => (
              <div key={b.band_id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <InputNumber size="small" min={0} max={99} style={{ width: 72 }} value={deps[b.band_id] || 0} onChange={(v) => setDeps((d) => ({ ...d, [b.band_id]: v || 0 }))} />
                <span style={{ fontSize: 12 }}>
                  {b.name} <Text type="secondary">{ageRangeLabel(b)}</Text> · {fmt(b.effective_price)} each
                  {b.override_price !== null ? <Tag color="blue" style={{ marginInlineStart: 6 }}>negotiated</Tag> : null}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {quoteError ? <Alert type="warning" showIcon message={quoteError} /> : null}
        {quote ? (
          <div style={{ background: '#f6f8fb', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>
            {quote.lines.map((l) => (
              <div key={`${l.role}-${l.band_id}`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{l.name}{l.quantity > 1 ? ` × ${l.quantity}` : ''}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(l.line_total)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', marginTop: 6, paddingTop: 6 }}>
              <Text strong>Total · {quote.members} member{quote.members === 1 ? '' : 's'}</Text>
              <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(quote.total)}</Text>
            </div>
          </div>
        ) : null}
        {offered.org_scoped ? (
          <Text type="secondary" style={{ fontSize: 11 }}>Only sub-bands priced for this organization are offered.</Text>
        ) : null}
      </Space>
    </div>
  );
}
